import { getApiClient } from "@src/api";
import { graphql } from "@src/gql/gql";
import { handleError } from "@src/utils/error-handling";
import { useMutation } from "@tanstack/react-query";

const createBillingPortalSessionMutation = graphql(/* GraphQL */ `
	mutation CreateBillingPortalSession($previewReturnUrl: String) {
		plans {
			createBillingPortalSession(previewReturnUrl: $previewReturnUrl)
		}
	}
`);

export type CreateBillingPortalSessionParams = {
	previewReturnUrl?: string;
};

/**
 * Mutation function for creating a Stripe billing portal session.
 */
export const createBillingPortalSessionMutationFn = async (
	params: CreateBillingPortalSessionParams = {},
) => {
	const client = getApiClient();
	const result = await client.mutate({
		mutation: createBillingPortalSessionMutation,
		variables: {
			previewReturnUrl: params.previewReturnUrl ?? null,
		},
	});

	return result.data?.plans.createBillingPortalSession;
};

type CreateBillingPortalSessionResult = Awaited<
	ReturnType<typeof createBillingPortalSessionMutationFn>
>;

export const useCreateBillingPortalSession = (
	onSuccess?: (
		result: CreateBillingPortalSessionResult,
	) => void | Promise<void>,
) =>
	useMutation({
		mutationFn: createBillingPortalSessionMutationFn,
		onError: (error: Error) => {
			handleError(error, "Failed to create billing portal session");
		},
		onSuccess: async (result) => {
			await onSuccess?.(result);
		},
	});
